import {
  Network,
  NetworkMembership,
} from "@converge-collective/common/models/Network";
import { isNetworkAdmin } from "@converge-collective/common/models/NetworkAdmin";
import {
  AccountBox,
  AdminPanelSettings,
  Delete,
  Email,
  Help,
  Logout,
  Settings,
} from "@mui/icons-material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import CircleIcon from "@mui/icons-material/Circle";
import GroupIcon from "@mui/icons-material/Group";
import {
  Alert,
  AlertTitle,
  Avatar,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { createStyles, makeStyles } from "@mui/styles";
import cx from "clsx";
import { isEmpty } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { MouseEvent, ReactElement, useState } from "react";
import {
  useAuth,
  useFirebaseApp,
  useFirestore,
  useFirestoreDoc,
  useUser,
} from "reactfire";
import AddSpeedDial from "~/components/AddSpeedDial";
import MobileNav from "~/components/MobileNav";
import useProfile, { useNetworksMemberships } from "~/hooks/useProfile";
import useUserClaims from "~/hooks/useUserClaims";
import { clearFirestoreCache } from "~/lib/browser-util";
import { createAlert } from "~/lib/globalAlerts";
import {
  mainNav,
  marketingNavItemsLeft,
  marketingNavItemsRight,
  networkAdminRoute,
} from "~/routes";
import Notifications from "./Notifications";
import { hasRN } from "./ReactNativeBridge";
import ToggleChatPanelButton from "./ToggleChatPanelButton";
import SignIn from "./auth/SignIn";
import UpdateEmail from "./user/UpdateEmail";
import { modelFromSnap } from "@converge-collective/common/util";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    navLink: {
      fontWeight: 400,
      marginLeft: 4,
      marginRight: 4,
      color: theme.palette.text.primary,
    },
    floatRightItem: {
      marginLeft: "auto",
      marginRight: 4,
    },
    accountButton: {
      marginRight: 4,
    },
    selectedLink: {
      "& span": {
        borderBottom: `2px solid ${theme.palette.primary.main}`,
        borderRadius: 0,
      },
    },
  })
);

// logged in nav requires a network to be set
export const LoggedInNav = (): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme();
  const isLargeDown = useMediaQuery(theme.breakpoints.down("lg"));
  const { data: user } = useUser();
  const [profile] = useProfile(user?.uid);
  const app = useFirebaseApp();
  const router = useRouter();
  const auth = useAuth();
  const firestore = useFirestore();

  const { claims, refreshClaims } = useUserClaims();

  const { name: fullName, currentNetworkMembership } = profile || {};
  // const { network: currentNetwork } = currentNetworkMembership || {};

  const { data: networkData } = useFirestoreDoc<Network>(
    firestore.doc(currentNetworkMembership?.network.ref?.path || "noop/noop"),
    {
      idField: "id",
    }
  );
  const network = networkData && modelFromSnap<Network>(networkData);

  const profileRef =
    profile && firestore.collection("profiles").doc(profile.id);

  const NetworkAdminItems = () => {
    if (
      !currentNetworkMembership ||
      !claims ||
      !isNetworkAdmin(claims, network?.id || "")
    ) {
      return null;
    }

    return network ? (
      <>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => {
            router.push(networkAdminRoute(network.slug));
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <AdminPanelSettings color="primary" />
          </ListItemIcon>
          <ListItemText>Network Admin</ListItemText>
        </MenuItem>
      </>
    ) : (
      <div />
    );
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
    // any time the user opens the user nav refresh their admin claims
    refreshClaims();
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const RNMenuItems = () => {
    const router = useRouter();
    return (
      <>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => {
            router.push("/activity-sync");
            handleMenuClose();
          }}
        >
          Activity Sync
        </MenuItem>
      </>
    );
  };

  const signOutClick = async () => {
    await auth.signOut();
    clearFirestoreCache();
    handleMenuClose();
    // router.reload("/");
    window.location.replace("/");
  };

  console.count("Render LoggedInNav");
  console.log("Header LoggedInNav", { user, profile, profileRef });
  const networkNav = network ? mainNav(network) : undefined;

  const [networkMemberships] = useNetworksMemberships(user?.uid);
  console.log("header networkMemberships", { networkMemberships });

  const setCurrentNetwork = async (nm: NetworkMembership) => {
    if (profileRef) {
      await profileRef.update({
        currentNetworkMembership: nm,
      });
      router.push(`/${nm.network.slug}`);
    } else {
      console.warn("unexpected missing profileRef", { profileRef });
    }
  };

  const [showSignIn, setShowSignIn] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const deleteAccount = () => {
    setShowDeleteAccount(true);
  };
  const [confirmDelete, setConfirmDelete] = useState("");
  const handleCloseDelete = () => {
    setShowDeleteAccount(false);
  };
  const [deleteError, setDeleteError] = useState("");
  const handleDeleteAccount = async () => {
    console.log("deleting your account!");
    try {
      await user.delete();

      app.analytics().logEvent("delete_account", { user });
      createAlert({
        message: "Your account was deleted. Please come back soon!",
        severity: "success",
      });
      router.push("/");
    } catch (e) {
      setDeleteError(e.message);
    }
  };
  const handleSignInClose = () => {
    setShowSignIn(false);
  };

  const handleMenuItemClick = (route: string) => () => {
    router.push(route);
    handleMenuClose();
  };

  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const handleChangeEmail = () => {
    setShowChangeEmail(true);
    handleMenuClose();
  };
  const handleChangeEmailClose = () => {
    setShowChangeEmail(false);
  };

  if (!user) return <></>;
  return (
    <>
      {!isLargeDown && (
        <>
          {profile?.currentNetworkMembership && (
            <>
              {networkNav?.map(({ url, onClick, label }) =>
                url ? (
                  <LinkCurrent href={url} key={url}>
                    <Button>{label}</Button>
                  </LinkCurrent>
                ) : (
                  <Button key={label} onClick={onClick}>
                    {label}
                  </Button>
                )
              )}
            </>
          )}
        </>
      )}

      <SignIn isOpen={showSignIn} handleClose={handleSignInClose} />

      <UpdateEmail
        isOpen={showChangeEmail}
        handleClose={handleChangeEmailClose}
      />

      <Dialog
        onClose={handleCloseDelete}
        open={showDeleteAccount}
        maxWidth="sm"
      >
        <DialogTitle>Are you sure you want to delete your account?</DialogTitle>
        <Alert severity="error">
          <AlertTitle>Warning!</AlertTitle>
          This action will permanantly delete your account and all associated
          data. Upon deletion your data will no longer be retrievable.
        </Alert>
        <DialogContent>
          <DialogContentText>
            To proceed, please type "confirm" in the box below:
          </DialogContentText>
          <TextField
            error={confirmDelete !== "confirm"}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setConfirmDelete(event.target.value);
            }}
            fullWidth
            placeholder="Type confirm to continue"
          />
          {!isEmpty(deleteError) && (
            <Alert severity="error" sx={{ my: 2 }}>
              <AlertTitle>
                Oops, something went wrong while trying to delete your account.
              </AlertTitle>
              {deleteError}
              <Button onClick={() => setShowSignIn(true)}>Sign in again</Button>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={handleCloseDelete}>
            Nevermind, I want to keep my account
          </Button>
          <Button
            onClick={handleDeleteAccount}
            disabled={confirmDelete !== "confirm"}
            color="error"
          >
            I understand, permanantly delete my account
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.floatRightItem}>
        {!isLargeDown && profile?.currentNetworkMembership?.network ? (
          <Chip
            icon={<GroupIcon />}
            color="secondary"
            size="small"
            label={profile?.currentNetworkMembership.network.name}
          />
        ) : (
          <div className={classes.floatRightItem} />
        )}
      </div>
      {isLargeDown && networkNav && <MobileNav networkNav={networkNav} />}

      {profile && <AddSpeedDial network={network} />}

      <Notifications />

      {!network?.settings?.isDirectMessagingDisabled ? (
        <ToggleChatPanelButton />
      ) : null}

      <IconButton
        aria-label="account of current user"
        className={classes.accountButton}
        aria-controls="menu-appbar"
        aria-haspopup="true"
        edge={false}
        onClick={handleMenuOpen}
        color="primary"
        size="large"
      >
        {user?.photoURL ? (
          // Note: user.photoURL can be a large image of any size, and doesn't
          // support `imageSize` the way profile.photoURL does.
          <Avatar src={user.photoURL} />
        ) : (
          <AccountCircle sx={{ fontSize: 40 }} color="primary" />
        )}
      </IconButton>
      <Menu
        id="menu-appbar"
        keepMounted
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleMenuItemClick("/user")}>
          <ListItemIcon>
            <AccountBox color="primary" />
          </ListItemIcon>
          <ListItemText>{fullName || "Profile"}</ListItemText>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <li style={{ padding: "6px 16px" }}>
          <Typography variant="subtitle2">{user?.email}</Typography>
        </li>
        <MenuItem onClick={handleChangeEmail}>
          <ListItemIcon>
            <Email color="primary" />
          </ListItemIcon>
          <ListItemText>Update Your Email</ListItemText>
        </MenuItem>
        {networkMemberships && networkMemberships.length > 0 && (
          <Divider sx={{ my: 1 }} />
        )}
        {networkMemberships?.map((nm, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              setCurrentNetwork(nm);
              handleMenuClose();
            }}
          >
            <ListItemText>
              {nm.network.name + " "}
              {nm.network.id === currentNetworkMembership?.network.id && (
                <CircleIcon
                  sx={{ marginLeft: 1, fontSize: 10 }}
                  color="secondary"
                />
              )}
            </ListItemText>
          </MenuItem>
        ))}
        {hasRN() && <RNMenuItems />}
        <NetworkAdminItems />
        <Divider sx={{ my: 1 }} />
        <MenuItem onClick={handleMenuItemClick("/onboarding/account")}>
          <ListItemIcon>
            <Settings color="primary" />
          </ListItemIcon>
          <ListItemText>Account Settings</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuItemClick("/support")}>
          <ListItemIcon>
            <Help color="primary" />
          </ListItemIcon>
          <ListItemText>Help & Support</ListItemText>
        </MenuItem>
        <MenuItem sx={{ color: "error" }} onClick={deleteAccount}>
          <ListItemIcon>
            <Delete color="primary" />
          </ListItemIcon>
          <ListItemText>Delete My Account</ListItemText>
        </MenuItem>
        <MenuItem onClick={signOutClick}>
          <ListItemIcon>
            <Logout color="primary" />
          </ListItemIcon>
          <ListItemText>Sign Out</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export const LoggedOutNav = ({
  setSignInVisible,
  setCreateAccountVisible,
}: {
  setSignInVisible: (b: boolean) => void;
  setCreateAccountVisible: (b: boolean) => void;
}): React.ReactElement => {
  const app = useFirebaseApp();
  return (
    <>
      <div style={{ marginLeft: "auto" }} />
      <Button onClick={() => setCreateAccountVisible(true)}>
        Create account
      </Button>
      <Button
        sx={{ ml: 1 }}
        onClick={() => {
          setSignInVisible(true);
          // const params = {};
          // must be called client side only
          app.analytics().logEvent("sign_in_click");
          console.log("sign_in_click");
        }}
      >
        Sign in
      </Button>
    </>
  );
};

export const MarketingNav = ({
  setSignInVisible,
  setCreateAccountVisible,
  networkSlug,
  showAuthNav,
  showLeftNav,
}: {
  setSignInVisible: (b: boolean) => void;
  setCreateAccountVisible: (b: boolean) => void;
  networkSlug: string;
  showAuthNav: boolean;
  showLeftNav: boolean;
}): React.ReactElement => {
  // const mobileAccountButtons: NavItem[] = [
  //   { label: "Create account", onClick: () => setCreateAccountVisible(true) },
  //   { label: "Sign in", onClick: () => setSignInVisible(true) },
  // ];

  const accountButtons = showAuthNav && (
    <>
      <Button onClick={() => setCreateAccountVisible(true)}>
        Create account
      </Button>
      <Button
        sx={{ mx: 1 }}
        onClick={() => {
          setSignInVisible(true);
          // const params = {};
          // must be called client side only
          app.analytics().logEvent("sign_in_click");
          console.log("sign_in_click");
        }}
      >
        Sign in
      </Button>
    </>
  );
  const app = useFirebaseApp();
  // TODO - pull the slug from the url instead of hardcoding

  const navRight = marketingNavItemsRight(networkSlug);
  const navLeft = showLeftNav ? marketingNavItemsLeft(networkSlug) : [];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return isMobile ? (
    <>
      <MobileNav networkNav={[...navRight, ...navLeft]} />
      <div style={{ marginLeft: "auto" }} />
      {accountButtons}
    </>
  ) : (
    <>
      {navLeft.map(({ url, onClick, label }) =>
        url ? (
          <Link href={url} key={url}>
            <Button>{label}</Button>
          </Link>
        ) : (
          <Button key={label} onClick={onClick}>
            {label}
          </Button>
        )
      )}
      <div style={{ marginLeft: "auto" }} />
      {accountButtons}
      {/* <Button */}
      {/*   href="https://buy.stripe.com/28oeYc5aRaoBaUobII" */}
      {/*   target="_blank" */}
      {/*   variant="contained" */}
      {/* > */}
      {/*   Fall Comp Registration */}
      {/* </Button> */}
    </>
  );
};

const LinkCurrent = ({
  href,
  children,
}: {
  href: string;
  children: ReactElement<any>;
}) => {
  const classes = useStyles();
  const router = useRouter();
  // let className = cx(childrenren.props.className || ''
  const linkClasses = [
    classes.navLink,
    router.asPath === href && classes.selectedLink,
  ];
  const className = cx(linkClasses);
  return <Link href={href}>{React.cloneElement(children, { className })}</Link>;
};
